import { CalendarCheck, CalendarPlus, Check, Heart, Loader, MessageCircle, Sparkles } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { MouseEvent } from 'react'
import { ScoreGauge } from '@/components/ds/ScoreGauge'
import { StatusChip, type VideoStatus } from '@/components/ds/StatusChip'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import { formatCompactNumber, formatDuration, formatShortDate, formatViewCount } from '@/lib/format'
import { preservingChannel } from '@/lib/routing'
import styles from './ProjectCard.module.scss'

export interface ProjectCardProps {
  videoId: string
  language: string
  title: string
  status: VideoStatus
  /** Generation progress step, shown in processing state. */
  stepLabel?: string | null
  /** Thumbnail image URL or a hex color for generated posters. */
  posterHex?: string | null
  thumbnailUrl?: string | null
  durationSec?: number | null
  views?: number | null
  /**
   * Visibilidade real no YouTube, repassada ao ``StatusChip``: num card
   * publicado ela troca rótulo, cor e ícone; o status (ui_status) não muda.
   */
  privacy?: 'public' | 'unlisted' | 'private' | null
  scheduledLabel?: string | null
  /** Tooltip shown on hover of the status chip when scheduled — full date + relative countdown. */
  scheduledTooltip?: string | null
  /** Ranking final_score (0..1). Null when the video hasn't been scored yet. */
  score?: number | null
  /** Part of the grid's multi-select — drawn with the orange border. */
  selected?: boolean
  /**
   * Makes the bottom bar a selection toggle; the poster keeps opening the
   * project. Leave it out for cards that can't be selected (mid-generation)
   * and the bar stays plain text.
   */
  onToggleSelect?: ((event: MouseEvent<HTMLButtonElement>) => void) | undefined
  /** De onde o vídeo é: do pool (ainda livre) ou o canal, na visão de vários canais. */
  origin?: { kind: 'pool' | 'channel'; label: string } | null
  /** Curtidas e comentários do YouTube, no overlay do hover da capa. */
  likes?: number | null
  comments?: number | null
  /** A data do overlay: a de publicação (vídeo publicado) ou a de criação. */
  dateInfo?: { kind: 'published' | 'created'; iso: string } | null
}

function statusConfig(status: VideoStatus): { label: string; icon?: React.ReactNode } {
  switch (status) {
    case 'publicado':
      return { label: 'Publicado' }
    case 'agendado':
      return { label: 'Agendado' }
    case 'gerando':
      return { label: 'Gerando', icon: <Loader /> }
    case 'rascunho':
      return { label: 'Rascunho' }
    case 'falhou':
      return { label: 'Falhou' }
    default:
      return { label: status }
  }
}

/**
 * Num card publicado, a visibilidade não listado/privado ganha do rótulo de
 * agendado: o ``StatusChip`` mostra o rótulo, o estilo e o ícone dela.
 */
function privacyWins(status: VideoStatus, privacy: ProjectCardProps['privacy']): boolean {
  return status === 'publicado' && (privacy === 'unlisted' || privacy === 'private')
}

export function ProjectCard({
  videoId,
  language,
  title,
  status,
  stepLabel,
  posterHex,
  thumbnailUrl,
  durationSec,
  views,
  privacy = null,
  scheduledLabel,
  scheduledTooltip,
  score,
  selected = false,
  onToggleSelect,
  origin = null,
  likes = null,
  comments = null,
  dateInfo = null,
}: ProjectCardProps) {
  const isProcessing = status === 'gerando'
  const config = statusConfig(status)
  const publishedViews = status === 'publicado' && views != null ? views : null
  const shortDate = dateInfo ? formatShortDate(dateInfo.iso) : null
  const DateIcon = dateInfo?.kind === 'published' ? CalendarCheck : CalendarPlus
  const hasHoverStats = likes != null || comments != null || shortDate != null
  const chipNode = (
    <StatusChip
      status={status}
      privacy={privacy}
      label={privacyWins(status, privacy) ? undefined : (scheduledLabel ?? config.label)}
    />
  )
  const posterStyle = thumbnailUrl
    ? { backgroundImage: `linear-gradient(180deg, transparent 40%, rgb(0 0 0 / 0.75) 100%), url(${thumbnailUrl})` }
    : posterHex
      ? {
          backgroundImage: `linear-gradient(180deg, transparent 40%, rgb(0 0 0 / 0.75) 100%), radial-gradient(circle at 40% 40%, ${posterHex} 0%, rgb(0 0 0 / 0.6) 60%, var(--bg) 95%)`,
        }
      : undefined

  const metaContent = (
    <>
      {onToggleSelect ? (
        <span className={styles.check} aria-hidden>
          {selected ? <Check /> : null}
        </span>
      ) : null}
      {scheduledTooltip ? (
        <Tooltip content={scheduledTooltip}>
          <span>{chipNode}</span>
        </Tooltip>
      ) : (
        chipNode
      )}
      {publishedViews != null ? (
        <span className={styles.metaRight}>
          {formatViewCount(publishedViews)}
        </span>
      ) : null}
    </>
  )

  return (
    <div className={cn(styles.card, selected && styles.selected)}>
      <Link
        to="/projects/$projectId"
        params={{ projectId: videoId }}
        search={preservingChannel({ lang: language })}
        className={cn(styles.poster, isProcessing && styles.processing)}
        style={posterStyle}
      >
        {isProcessing ? (
          <div className={styles.processingContent}>
            <div className={styles.processingGlyph} aria-hidden>
              <Loader />
            </div>
            <div className={styles.processingTitle}>{title}</div>
            {stepLabel ? (
              <div className={styles.processingStep}>
                <Sparkles aria-hidden /> {stepLabel}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            {durationSec ? <div className={styles.badge}>{formatDuration(durationSec)}</div> : null}
            {score != null ? (
              <div className={styles.scoreBadge}>
                <ScoreGauge score={score} size={36} />
              </div>
            ) : null}
            {origin ? (
              <div className={cn(styles.origin, origin.kind === 'pool' && styles.originPool)}>{origin.label}</div>
            ) : null}
            <div className={styles.title}>{title}</div>
            {hasHoverStats ? (
              // Decorativo: o mesmo dado está na página do projeto.
              <div className={styles.hoverStats} aria-hidden>
                {likes != null ? (
                  <span className={styles.hoverStat}>
                    <Heart /> {formatCompactNumber(likes)}
                  </span>
                ) : null}
                {comments != null ? (
                  <span className={styles.hoverStat}>
                    <MessageCircle /> {formatCompactNumber(comments)}
                  </span>
                ) : null}
                {shortDate != null ? (
                  <span className={styles.hoverStat}>
                    <DateIcon /> {shortDate}
                  </span>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Link>
      {onToggleSelect ? (
        <button
          type="button"
          className={cn(styles.meta, styles.metaToggle)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Desmarcar' : 'Selecionar'}: ${title}`}
          onClick={onToggleSelect}
          onMouseDown={(event) => {
            // Shift-click selects a range; without this the browser also
            // extends a text selection across the cards in between.
            if (event.shiftKey) event.preventDefault()
          }}
        >
          {metaContent}
        </button>
      ) : (
        <div className={styles.meta}>{metaContent}</div>
      )}
    </div>
  )
}
